import { registerDecorators as _registerDecorators, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./input.html";
import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
import { validateEmail } from '~/modules/utilities/validation_utils';
import { BaseInput, getBooleanAttr, getNumberAttr } from '../common/common';
import input from './input.html';
import inputCheckbox from './inputCheckbox.html';
import inputRadio from './inputRadio.html';
export let InputType = /*#__PURE__*/function (InputType) {
  InputType["Text"] = "text";
  InputType["Checkbox"] = "checkbox";
  InputType["Radio"] = "radio";
  InputType["Number"] = "number";
  InputType["Email"] = "email";
  InputType["Telephone"] = "tel";
  return InputType;
}({});
class Input extends BaseInput {
  constructor(...args) {
    super(...args);
    this.hasSlots = {};
    this.type = InputType.Text;
    this.placeholder = void 0;
    this._value = '';
    this.tabIndex = 0;
    this.min = void 0;
    this.max = void 0;
    this.lastNRemaining = void 0;
    this.formatErrorMessage = void 0;
    this.charactersRemainingText = () => `${this.remainingCharacters} characters remaining`;
  }
  set variant(value) {
    this._variant = value;
    if (this._variant === 'inverse') {
      this.template.host.style.setProperty('--ads-background-color', 'var(--ads-checkbox-inverse-color)');
      this.template.host.style.setProperty('--ads-checked-color', 'var(--ads-inverse-primary-color)');
    }
  }
  get variant() {
    return this._variant;
  }
  set value(value) {
    this._value = value;
    if (this.maxLength) {
      this.remainingCharacters = this._maxlength - this._value.length;
    }
  }
  get value() {
    return this._value;
  }
  set maxLength(value) {
    if (value) {
      this._maxlength = getNumberAttr(value);
    }
  }
  get maxLength() {
    return this._maxlength;
  }
  set checked(value) {
    this._checked = getBooleanAttr(value);
    if (this._checked) {
      this.cleanError();
    } else if (this._required) {
      this.setError(this.requiredMessage);
    }
  }
  get checked() {
    return this._checked;
  }
  set showRemainingCharacters(value) {
    this._showRemainingCharacters = getBooleanAttr(value);
  }
  get showRemainingCharacters() {
    return this._showRemainingCharacters;
  }
  get showRemainingLastN() {
    if (this.lastNRemaining) {
      return this._value.length >= this._maxlength - this.lastNRemaining;
    }
    return true;
  }
  get showRemainingCharactersInfo() {
    if (this.showRemainingCharacters && this.showRemainingLastN) {
      return this.showRemainingCharacters;
    }
    return false;
  }
  get charactersRemainingDescription() {
    return this.charactersRemainingText();
  }
  connectedCallback() {
    super.connectedCallback();
    if (this.maxLength) {
      this.remainingCharacters = this._maxlength - this._value.length;
    }
  }
  render() {
    switch (this.type) {
      case 'checkbox':
        return inputCheckbox;
      case 'radio':
        return inputRadio;
      default:
        return input;
    }
  }
  handleSlotChange(event) {
    const slot = event.target;
    this.hasSlots[slot.name] = slot.assignedElements().length !== 0;
    if (slot.name === 'left') {
      this.template.host.style.setProperty('--ads-border-radius-left', '0px');
    }
    if (slot.name === 'right') {
      this.template.host.style.setProperty('--ads-border-radius-right', '0px');
    }
  }
  handleChange(event) {
    event.preventDefault();
    const newEvent = new CustomEvent(event.type, {
      detail: event.target.value
    });
    this.dispatchEvent(newEvent);
  }
  handleCheckboxChange(event) {
    event.preventDefault();
    // We must store the updated value before re-dispatching events because external callers may access its value via the checked property
    this._checked = event.target.checked;
    this.evaluateRequired();
    const newEvent = new CustomEvent(event.type, {
      detail: event.target.checked
    });
    this.dispatchEvent(newEvent);
  }
  handleInput(event) {
    event.preventDefault();
    const target = event.target;
    this.remainingCharacters = this._maxlength - target.value.length;
    this._value = target.value;
  }
  handleBlur() {
    super.handleBlur();
    if (this.type === InputType.Checkbox) {
      if (this._disabled || this.checked) {
        this.cleanError();
      }
      if (!this._disabled && this._required && !this.checked) {
        this.setError(this.requiredMessage);
      }
      return;
    }
    if (this.type === InputType.Email) {
      const isValid = validateEmail(this._value);
      if (this._disabled || isValid) {
        this.cleanError();
      }
      if (!this._disabled && !validateEmail(this._value)) {
        if (this.formatErrorMessage) {
          this.setError(this.formatErrorMessage);
          return;
        }
        throwDevelopmentOrLog(new Error("Invalid email detected, but 'formatErrorMessage' value is missing, so no error message can be displayed."));
      }
    }
  }
  evaluateRequired() {
    if (this.type !== InputType.Checkbox) {
      super.evaluateRequired();
      return;
    }
    if (!this._disabled && this._forceShowError && this._required && !this._checked) {
      this.setError(this.requiredMessage);
    } else {
      this.cleanError();
    }
  }
  /*LWC compiler v7.1.3*/
}
Input.delegatesFocus = true;
_registerDecorators(Input, {
  publicProps: {
    type: {
      config: 0
    },
    placeholder: {
      config: 0
    },
    tabIndex: {
      config: 0
    },
    min: {
      config: 0
    },
    max: {
      config: 0
    },
    lastNRemaining: {
      config: 0
    },
    formatErrorMessage: {
      config: 0
    },
    charactersRemainingText: {
      config: 0
    },
    variant: {
      config: 3
    },
    value: {
      config: 3
    },
    maxLength: {
      config: 3
    },
    checked: {
      config: 3
    },
    showRemainingCharacters: {
      config: 3
    }
  },
  track: {
    hasSlots: 1
  },
  fields: ["_value"]
});
const __lwc_component_class_internal = _registerComponent(Input, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;